import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { loginSchema, type LoginFormValues } from '@/schemas/login';
import { useAuth } from '@/contexts/AuthContext';
import { getApiErrorMessage } from '@/lib/api-error';
import { feedback } from '@/lib/feedback';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { BrandLogo } from '@/components/brand/BrandLogo';

const fieldClass =
  'border-[#d9d4cc] bg-white text-[#16181d] placeholder:text-[#5c5852] focus-visible:ring-[#2b9ce1]';

export function LoginPage() {
  usePageTitle('Entrar');
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', senha: '' },
  });

  if (isAuthenticated) {
    return <Navigate to="/painel" replace />;
  }

  const onSubmit = async (data: LoginFormValues) => {
    try {
      const result = await login(data.email, data.senha);
      if (result.primeiroAcesso) {
        navigate('/redefinir-senha', { replace: true });
      } else if (result.perfis.includes('SUPERADMIN')) {
        navigate('/superadmin', { replace: true });
      } else {
        navigate('/painel', { replace: true });
      }
    } catch (e) {
      feedback.error(getApiErrorMessage(e));
    }
  };

  return (
    <div className="site-public flex min-h-screen flex-col">
      <header className="border-b border-[#d9d4cc]">
        <div className="mx-auto flex h-[4.25rem] w-full max-w-6xl items-center justify-between px-4">
          <Link to="/" className="flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2b9ce1]">
            <BrandLogo variant="compact" className="h-8" />
          </Link>
          <Link
            to="/"
            className="text-sm text-[#5c5852] transition-colors duration-150 hover:text-[#16181d]"
          >
            ← Voltar ao site
          </Link>
        </div>
      </header>

      <main className="flex flex-1 items-center px-4 py-12 sm:px-10">
        <div
          className="mx-auto w-full max-w-[400px]"
          style={{ animation: 'fade-in-up 220ms ease-out both' }}
        >
          <p className="text-sm text-[#5c5852]">Sua oficina</p>
          <h1 className="mt-2 font-display text-[clamp(2rem,4vw,3rem)] font-semibold tracking-tight text-[#16181d]">
            Entrar
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-[#5c5852]">
            Use o e-mail e a senha da sua conta.
          </p>

          <form className="mt-8 space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[#16181d]">
                E-mail
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email')}
                aria-invalid={!!errors.email}
                className={errors.email ? `${fieldClass} border-destructive` : fieldClass}
              />
              {errors.email && (
                <p className="flex items-center gap-1.5 text-sm text-destructive" role="alert">
                  <AlertCircle className="h-4 w-4" aria-hidden />
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="senha" className="text-[#16181d]">
                Senha
              </Label>
              <Input
                id="senha"
                type="password"
                autoComplete="current-password"
                {...register('senha')}
                aria-invalid={!!errors.senha}
                className={errors.senha ? `${fieldClass} border-destructive` : fieldClass}
              />
              {errors.senha && (
                <p className="flex items-center gap-1.5 text-sm text-destructive" role="alert">
                  <AlertCircle className="h-4 w-4" aria-hidden />
                  {errors.senha.message}
                </p>
              )}
            </div>

            <Button type="submit" variant="default" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Entrando…' : 'Entrar'}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
